import { DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BusinessAccount } from '@/app/common/general-types/business-account.dto';
import { CampaignManagerDto } from '@/app/common/general-types/campaigns.dto';
import { UserRolesEnum } from '@/app/common/general-types/users.dto';
import { ImageService } from '@/app/services/image/image.service';
import { TooltipDirective } from '@/app/common/directives/tooltip/tooltip.directive';
import { FirebaseService } from '@/app/common/firebase/firebase.service';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { AccountService } from '@/app/services/account/account.service';
import { Router } from '@angular/router';
import { SHOUT_INTERNAL_ACCESS_ROLES } from '@/app/common/general-types/users.dto';
import { Subject, catchError, map, of, switchMap, tap } from 'rxjs';
import { BrandInsightsState } from '../../brand-insights.state';

type TeamLoadResult = {
  status: 'idle' | 'loaded' | 'error';
  users: CampaignManagerDto[];
};

@Component({
  selector: 'brand-ins-team',
  imports: [DatePipe, TooltipDirective],
  templateUrl: './team.html',
  styleUrl: './team.css',
  host: { class: 'block h-full min-h-0 w-full min-w-0' },
})
export class Team implements OnChanges {
  @Input() brand: BusinessAccount | undefined;

  readonly imageService = inject(ImageService);
  readonly firebaseService = inject(FirebaseService);
  readonly currentUserService = inject(CurrentUserService);
  private readonly accountService = inject(AccountService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly brandState = inject(BrandInsightsState);
  readonly shoutInternalAccessRoles = SHOUT_INTERNAL_ACCESS_ROLES;

  members = signal<CampaignManagerDto[]>([]);
  loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');

  pendingImpersonation = signal<{ uid: string; displayName: string } | null>(null);
  apiKeyInput = signal<string>('');
  isLoading = signal<boolean>(false);
  impersonateError = signal<string | null>(null);

  private readonly loadBrandId$ = new Subject<string | null>();

  constructor() {
    effect(() => {
      const fromState = this.brandState.brand();
      if (fromState && fromState.id !== this.brand?.id) {
        this.brand = fromState;
        this.loadBrandId$.next(fromState.id);
      }
    });

    this.loadBrandId$
      .pipe(
        tap((brandId) => {
          this.members.set([]);
          this.loadingState.set(brandId ? 'loading' : 'idle');
          this.refreshView();
        }),
        switchMap((brandId) => {
          if (!brandId) {
            return of<TeamLoadResult>({ status: 'idle', users: [] });
          }
          return this.accountService.getUsersForBusinessAccount(brandId).pipe(
            map(
              (users): TeamLoadResult => ({
                status: 'loaded',
                users: (users as CampaignManagerDto[]) ?? [],
              }),
            ),
            catchError((err) => {
              console.error('Error fetching brand team members:', err);
              return of<TeamLoadResult>({ status: 'error', users: [] });
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.members.set(result.users);
        this.loadingState.set(result.status);
        this.refreshView();
      });
  }

  private refreshView(): void {
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['brand']) {
      return;
    }
    this.loadBrandId$.next(this.brand?.id ?? null);
  }

  teamMembers(): CampaignManagerDto[] {
    return this.members().filter((m) => !m.isDeleted);
  }

  userRoleLabel(role: number | undefined): string {
    if (role === undefined || role === null) {
      return '—';
    }
    const label = (UserRolesEnum as Record<number, string>)[role];
    return label ?? `Role ${role}`;
  }

  memberDisplayName(member: CampaignManagerDto): string {
    const u = member.userAccount;
    if (!u) {
      return '—';
    }
    const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
    if (name) {
      return name;
    }
    return u.email ?? '—';
  }

  openUserProfile(member: CampaignManagerDto): void {
    const uid = member.userAccount?.firebaseUid;
    if (!uid) {
      return;
    }
    void this.router.navigate(['/admin-options/user-profile', uid], {
      state: { profileReturnUrl: this.router.url },
    });
  }

  triggerImpersonation(uid: string, displayName: string) {
    this.pendingImpersonation.set({ uid, displayName });
    this.apiKeyInput.set('');
    this.impersonateError.set(null);
  }

  async confirmImpersonation() {
    const pending = this.pendingImpersonation();
    if (!pending || !this.apiKeyInput()) return;
    this.isLoading.set(true);
    this.impersonateError.set(null);
    try {
      await this.firebaseService.impersonateFromApp(
        pending.uid,
        pending.displayName,
        this.apiKeyInput(),
        this.brand?.id,
      );
      this.pendingImpersonation.set(null);
      this.apiKeyInput.set('');
    } catch {
      this.impersonateError.set('Failed to impersonate. Check your API key and try again.');
    } finally {
      this.isLoading.set(false);
    }
  }

  cancelImpersonation() {
    this.pendingImpersonation.set(null);
    this.apiKeyInput.set('');
    this.impersonateError.set(null);
  }
}
